import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useBranding } from '../context/BrandingContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Shield, 
  Lock, 
  Eye, 
  EyeOff, 
  Loader2, 
  Mail, 
  ArrowRight,
  AlertTriangle,
  Sun,
  Moon
} from 'lucide-react';

export default function AdminLoginPage() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { branding } = useBranding();
  const navigate = useNavigate();

  const BrandName = () => {
    const name = branding.brand_name || 'AwadhPay';
    const m = /^(.*?)(pay)$/i.exec(name);
    return m ? <>{m[1]}<span className="text-red-400">{m[2]}</span></> : <>{name}</>;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.email) {
      newErrors.email = 'Admin email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // Only allow admin roles on this login page - merchants must use /login
      await login(formData.email, formData.password, ['super_admin', 'risk_manager', 'finance_manager']);
      
      toast.success('Welcome back, Admin!');
      navigate('/admin');
    } catch (error) {
      // Check if this is a role restriction error
      if (error.isRoleError) {
        toast.error('This portal is for administrators only.');
        setErrors({ submit: 'Access denied. Merchants please use the Merchant Login at /login.' });
      } else {
        const message = error.response?.data?.detail || 'Invalid admin credentials';
        toast.error(message);
        setErrors({ submit: message });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* Left Panel - Admin Visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-red-900/20 via-slate-900 to-slate-800">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-red-500/10 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-sm bg-red-500/20 flex items-center justify-center border border-red-500/30">
              <Shield className="w-5 h-5 text-red-400" />
            </div>
            <span className="text-2xl font-heading font-bold tracking-tight text-white">
              <BrandName />
            </span>
          </Link>
          
          {/* Middle Content */}
          <div className="space-y-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm mb-6">
                <AlertTriangle className="w-4 h-4" />
                Restricted Access
              </div>
              <h2 className="text-4xl font-heading font-bold leading-tight mb-4 text-white">
                Administrator
                <span className="text-red-400 block">Control Panel</span>
              </h2>
              <p className="text-lg text-slate-400 max-w-md">
                Secure access point for platform administrators. Unauthorized access attempts are logged and monitored.
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-8 h-8 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-red-400" />
                </div>
                <span>Multi-factor authentication</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-8 h-8 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <Lock className="w-4 h-4 text-red-400" />
                </div>
                <span>Role-based access control</span>
              </div>
              <div className="flex items-center gap-3 text-slate-400">
                <div className="w-8 h-8 rounded-sm bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                </div>
                <span>Audit logging enabled</span>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <p className="text-sm text-slate-500">
            © 2026 {branding.brand_name}. Admin Portal v2.0
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex flex-col bg-slate-900">
        {/* Top Bar */}
        <div className="flex items-center justify-between p-6">
          <Link to="/" className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-red-500/20 flex items-center justify-center border border-red-500/30">
              <Shield className="w-4 h-4 text-red-400" />
            </div>
            <span className="text-xl font-heading font-bold text-white">
              <BrandName />
            </span>
          </Link>
          <div className="flex items-center gap-3 ml-auto">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="rounded-sm text-slate-400 hover:text-white hover:bg-slate-800">
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Form Container */}
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
                <Shield className="w-8 h-8 text-red-400" />
              </div>
              <h1 className="text-3xl font-heading font-bold mb-2 text-white">Admin Login</h1>
              <p className="text-slate-400">Enter your administrator credentials</p>
            </div>

            {/* Login Form Card */}
            <Card className="border-slate-700/50 bg-slate-800/50 shadow-xl">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="admin-email" 
                      className={`text-sm transition-colors text-slate-300 ${focusedField === 'email' ? 'text-red-400' : ''}`}
                    >
                      Admin Email
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="admin-email"
                        type="email"
                        placeholder="admin@awadhpay.com"
                        value={formData.email}
                        onChange={(e) => {
                          setFormData({ ...formData, email: e.target.value });
                          if (errors.email) setErrors({ ...errors, email: null });
                        }}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 h-12 rounded-sm transition-all bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500 focus-visible:ring-red-500/20 ${
                          errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }`}
                        data-testid="admin-login-email-input"
                      />
                    </div>
                    {errors.email && (
                      <p className="text-xs text-red-400 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 rounded-full bg-red-400" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="space-y-2">
                    <Label 
                      htmlFor="admin-password"
                      className={`text-sm transition-colors text-slate-300 ${focusedField === 'password' ? 'text-red-400' : ''}`}
                    >
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="admin-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => {
                          setFormData({ ...formData, password: e.target.value });
                          if (errors.password) setErrors({ ...errors, password: null });
                        }}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        className={`pl-10 pr-10 h-12 rounded-sm transition-all bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-red-500 focus-visible:ring-red-500/20 ${
                          errors.password ? 'border-red-500 focus-visible:ring-red-500' : ''
                        }`}
                        data-testid="admin-login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                        tabIndex={-1}
                        data-testid="admin-toggle-password-visibility"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-xs text-red-400 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 rounded-full bg-red-400" />
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {/* Submit Error */}
                  {errors.submit && (
                    <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/20 text-sm text-red-400">
                      {errors.submit}
                    </div>
                  )}

                  {/* Login Button */}
                  <Button 
                    type="submit" 
                    className="w-full h-12 rounded-sm bg-red-600 hover:bg-red-700 text-white text-base font-semibold" 
                    disabled={loading}
                    data-testid="admin-login-submit-btn"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Authenticating...</>
                    ) : (
                      <>Access Admin Panel <ArrowRight className="ml-2 h-4 w-4" /></>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Bottom Links */}
            <div className="text-center mt-6 space-y-2">
              <p className="text-sm text-slate-500">
                Not an administrator?{' '}
                <Link to="/login" className="text-red-400 font-medium hover:underline" data-testid="merchant-login-link">
                  Merchant Login
                </Link>
              </p>
              <p className="text-xs text-slate-600">
                All login attempts are logged for security purposes
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
